import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE, apiHeaders, apiGet, apiFetch, apiPost, apiPut, apiDelete, leseFallback, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { useIsMobile } from '../../lib/hooks';
import { useZeitraster } from '../../lib/zeitraster';
import { FEHLZEITEN_GRENZEN_KEY, ladeFehlzeitenGrenzen, urlBase64ToUint8Array } from '../../lib/utils';
import { oeffneBoardAnpassen } from '../heute/board/anpassen';
import { Unterrichtszeiten } from './Unterrichtszeiten';
import { SchulfreieTage } from './SchulfreieTage';
import { AppKonfig, KlasseFachEintrag, NutzerProfil, Schulform } from '../../types';
import { LdSheet } from '../../components/ui/LdSheet';
import { globalNav } from '../../lib/nav';
import { MIcon } from '../../lib/micon';
import { RechtsLinks } from '../rechtliches/RechtsTexte';
import { ElternSprechzeitSettings, Sprechzeit, sprechzeitKurz } from './ElternSprechzeitSettings';
import { LdFeld, LdEingabe, LdAuswahl, LdChips, LdFensterFuss } from '../../components/ld/LdFenster';
import { KkMenu } from '../klasse/KlassenKopf';
import { EinstSpeichernZiel, EinstSpeichern, EinstZeile, EinstSchalter, EinstAbschnitt } from './einstellungen-rahmen';
import './einstellungen.css';

interface KlassenMitglied { id: number; vorname: string; nachname: string; email: string; fach: string; rolle: string; zugangsart: string; }
interface EinladungInfo   { id: number; klasse_id: number; klasse_name: string; fach: string; rolle: string; status: string; einladender_vorname: string; einladender_nachname: string; einladender_schule: string; }

// ── Welle D2 (Fix 3): KI-Monats-Kontingente aus S3 sichtbar machen ──────────
interface KiNutzung {
  plan: string;
  monat: string;
  nutzung: Record<string, { verbraucht: number; limit: number }>;
}
const KONT_TYPEN: { key: string; label: string }[] = [
  { key: 'diktat',     label: 'Diktate' },
  { key: 'mail',       label: 'Mail-KI' },
  { key: 'werkzeuge',  label: 'Werkzeuge' },
];
const PLAN_LABELS: Record<string, string> = { beta: 'Beta', free: 'Free', pro: 'Pro' };
// Welle K5: schmale Balken statt Kreise
function KiKontingentePanel() {
  const [daten, setDaten] = useState<KiNutzung | null>(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    apiGet<KiNutzung>('/api/ki-nutzung').then(setDaten).catch(() => setFehler(true));
  }, []);

  if (fehler) return <p className="ld-fe-text">Kontingente konnten nicht geladen werden.</p>;
  if (!daten) return <p className="ld-fe-text">Lädt …</p>;
  return (
    <>
      <div className="ld-es-liste">
        {KONT_TYPEN.map(t => {
          const n = daten.nutzung[t.key] ?? { verbraucht: 0, limit: 0 };
          const anteil = n.limit > 0 ? Math.min(1, n.verbraucht / n.limit) : 0;
          return (
            <div key={t.key} className="ld-es-kont">
              <div className="ld-es-kont__kopf"><b>{t.label}</b><span>{n.verbraucht} von {n.limit}</span></div>
              <div className={`ld-es-kont__spur${anteil >= 0.8 ? ' ist-knapp' : ''}`} role="img" aria-label={`${t.label}: ${n.verbraucht} von ${n.limit} verbraucht`}>
                <i style={{ width: `${Math.round(anteil * 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="ld-fe-text">Plan: <b>{PLAN_LABELS[daten.plan] ?? daten.plan}</b>. Die Kontingente setzen sich am 1. des Monats zurück.</p>
    </>
  );
}

export function EinstellungenAnsicht({
  konfig, onKonfigUpdate, onNeuStart, onAbmelden, nutzer, onKlassenReload,
}: {
  konfig: AppKonfig;
  onKonfigUpdate: (k: AppKonfig) => void;
  onNeuStart: () => void;
  onAbmelden: () => void;
  nutzer?: NutzerProfil | null;
  onKlassenReload?: () => void;
}) {
  const [bestaetigung, setBestaetigung] = useState(false);
  const [meineKlassen, setMeineKlassen] = useState<KlasseFachEintrag[]>([]);
  const [gmailStatus,   setGmailStatus]   = useState<{ verbunden: boolean; email?: string; status?: string } | null>(null);
  const [gmailLaden,    setGmailLaden]    = useState(false);
  const [syncPromotions, setSyncPromotions] = useState(false);
  // KI-Einwilligung
  const [kiErlaubt, setKiErlaubt] = useState<boolean | null>(null);
  const [kiToggleLaden, setKiToggleLaden] = useState(false);
  // Push Notifications
  const [pushAktiv,    setPushAktiv]    = useState(false);
  const [pushLaden,    setPushLaden]    = useState(false);
  const [pushEinst,    setPushEinst]    = useState({ fehlzeiten: true, entschuldigungen: true, aktionen: true, nachschreiben: true, mdl_luecken: true, mdl_vorschlaege: true });
  // Fehlzeiten-Grenzwerte
  const [fzGrenzen, setFzGrenzen] = useState(() => ladeFehlzeitenGrenzen());
  const [fzStundenProTag, setFzStundenProTag] = useState(6);   // Welle U3c
  const [fzGespeichert, setFzGespeichert] = useState(false);
  const [accountName, setAccountName] = useState(() =>
    nutzer ? `${nutzer.vorname} ${nutzer.nachname}` : (konfig.lehrer.name || '')
  );
  const [anrede, setAnrede] = useState<'frau' | 'herr' | ''>('');   // Welle Z2
  const [accountSchulform, setAccountSchulform] = useState<Schulform>(konfig.lehrer.schulform || 'Gymnasium');
  const [neuKlasse,        setNeuKlasse]        = useState('');
  const [neuFaecher,       setNeuFaecher]       = useState<string[]>([]);
  const [neuAktuellesFach, setNeuAktuellesFach] = useState('');
  const [neuRolle, setNeuRolle]                 = useState<'klassenleiter' | 'fachlehrer'>('fachlehrer');
  const [neuSchuljahr, setNeuSchuljahr]         = useState('');
  const [klassenLaden, setKlassenLaden] = useState(false);
  const [editKlasse,   setEditKlasse]   = useState<KlasseFachEintrag | null>(null);
  const [editName,     setEditName]     = useState('');
  const [editRolle,    setEditRolle]    = useState<'klassenleiter' | 'fachlehrer'>('fachlehrer');
  const [editLaden,    setEditLaden]    = useState(false);
  // SMTP-Konten
  const [smtpProvider,   setSmtpProvider]   = useState('');
  const [smtpEmail,      setSmtpEmail]      = useState('');
  const [smtpPassword,   setSmtpPassword]   = useState('');
  const [smtpLaden,      setSmtpLaden]      = useState(false);
  const [smtpKonten,     setSmtpKonten]     = useState<any[]>([]);
  const [smtpPresets,    setSmtpPresets]    = useState<Record<string, any>>({});

  // Shared Space State
  const [einladungen,      setEinladungen]      = useState<EinladungInfo[]>([]);
  const [mitgliederMap,    setMitgliederMap]    = useState<Record<number, KlassenMitglied[]>>({});
  const [einladeModal,     setEinladeModal]     = useState<{ klasse_id: number; klasse_name: string } | null>(null);
  const [einladeEmail,     setEinladeEmail]     = useState('');
  const [einladeFach,      setEinladeFach]      = useState('');
  const [einladeRolle,     setEinladeRolle]     = useState<'fachlehrer' | 'klassenleiter'>('fachlehrer');
  const [einladeLaden,     setEinladeLaden]     = useState(false);
  const [einladeFehler,    setEinladeFehler]    = useState('');
  const [einladeErfolg,    setEinladeErfolg]    = useState('');

  // Eigene Klassen als Klassenleiter (für Einladen-Button)
  const meineEigenenKlassen = Array.from(
    new Map(meineKlassen.filter(k => k.rolle === 'klassenleiter').map(k => [k.klasse_id, k])).values()
  );

  // Gruppiert nach klasse_id — eine Karte pro Klasse
  const klassenGruppiert = useMemo(() => {
    const kMap = new Map<number, {
      klasse_id: number;
      klasse_name: string;
      schuljahr: string;
      rolle: 'klassenleiter' | 'fachlehrer';
      faecher: Array<{ id: number; fach: string }>;
      konsistenzWarnung?: boolean;
    }>();
    for (const eintrag of meineKlassen) {
      const existing = kMap.get(eintrag.klasse_id);
      if (!existing) {
        kMap.set(eintrag.klasse_id, {
          klasse_id:  eintrag.klasse_id,
          klasse_name: eintrag.klasse_name,
          schuljahr:  eintrag.schuljahr,
          rolle:      eintrag.rolle,
          faecher:    [{ id: eintrag.id, fach: eintrag.fach }],
        });
      } else {
        existing.faecher.push({ id: eintrag.id, fach: eintrag.fach });
        if (existing.rolle !== eintrag.rolle) {
          if (eintrag.rolle === 'klassenleiter') existing.rolle = 'klassenleiter';
          existing.konsistenzWarnung = true;
          console.warn(`Klasse ${eintrag.klasse_name}: gemischte Rollen für Lehrer detektiert`);
        }
      }
    }
    return Array.from(kMap.values()).map(k => ({
      ...k,
      faecher: Array.from(new Map(k.faecher.map(f => [f.fach, f])).values()),
    }));
  }, [meineKlassen]);

  React.useEffect(() => {
    fetch(`${API_BASE}/api/meine-klassen`, { headers: apiHeaders() })
      .then(leseFallback<KlasseFachEintrag[]>([], '/api/meine-klassen'))
      .then((data: KlasseFachEintrag[]) => setMeineKlassen(data))
      .catch(() => {});
    fetch(`${API_BASE}/api/meine-einladungen`, { headers: apiHeaders() })
      .then(leseFallback<EinladungInfo[]>([], '/api/meine-einladungen'))
      .then((data: EinladungInfo[]) => setEinladungen(data))
      .catch(() => {});
    fetch(`${API_BASE}/api/lehrer/einstellungen`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/lehrer/einstellungen'))
      .then(data => {
        if (!data) return;
        setFzGrenzen({ achtung: data.schwellwertGelb, kritisch: data.schwellwertRot });
        if (data.stundenProTag) setFzStundenProTag(data.stundenProTag);
        setAnrede(data.anrede ?? '');   // Welle Z2
      })
      .catch(() => {});
    fetch(`${API_BASE}/api/gmail/status`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/gmail/status'))
      .then(data => { if (data) setGmailStatus(data); })
      .catch(() => {});
    fetch(`${API_BASE}/api/lehrer/mail-einstellungen`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/lehrer/mail-einstellungen'))
      .then(data => { if (data) setSyncPromotions(!!data.sync_promotions); })
      .catch(() => {});
    fetch(`${API_BASE}/api/einwilligung/status`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/einwilligung/status'))
      .then(data => { if (data !== null) setKiErlaubt(data.ki_verarbeitung_akzeptiert === true); })
      .catch(() => {});
    fetch(`${API_BASE}/api/mail/accounts`, { headers: apiHeaders() })
      .then(leseFallback<{ accounts: any[] }>({ accounts: [] }, '/api/mail/accounts'))
      .then(d => setSmtpKonten(d.accounts ?? []))
      .catch(() => {});
    fetch(`${API_BASE}/api/mail/providers`, { headers: apiHeaders() })
      .then(leseFallback<{ providers: any[] }>({ providers: [] }, '/api/mail/providers'))
      .then(d => {
        const map: Record<string, any> = {};
        (d.providers ?? []).forEach((p: any) => { map[p.key] = p; });
        setSmtpPresets(map);
      }).catch(() => {});
    // Push-Status laden
    fetch(`${API_BASE}/api/push/status`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/push/status'))
      .then(data => {
        if (data?.aktiv) {
          setPushAktiv(true);
          if (data.einstellungen) setPushEinst(data.einstellungen);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mitglieder laden wenn meineEigenenKlassen bekannt sind
  React.useEffect(() => {
    if (meineKlassen.length === 0) return;
    const uniqueKlassenIds = Array.from(new Set(meineKlassen.map(k => k.klasse_id)));
    uniqueKlassenIds.forEach(kid => {
      fetch(`${API_BASE}/api/klasse/${kid}/mitglieder`, { headers: apiHeaders() })
        .then(leseFallback<KlassenMitglied[]>([], '/api/klasse/:id/mitglieder'))
        .then((data: KlassenMitglied[]) => setMitgliederMap(prev => ({ ...prev, [kid]: data })))
        .catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meineKlassen.length]);

  async function gmailVerbinden() {
    setGmailLaden(true);
    try {
      const { url } = await apiGet<{ url: string }>('/api/gmail/auth-url');
      window.location.href = url;
    } catch (err) {
      zeigeApiFehler(err, 'Gmail-Verbindung fehlgeschlagen');
      setGmailLaden(false);
    }
  }

  async function gmailTrennen() {
    setGmailLaden(true);
    try {
      await apiDelete('/api/gmail/disconnect');
      setGmailStatus({ verbunden: false });
      toast('Gmail-Verbindung getrennt', 'success');
    } catch (e) {
      zeigeApiFehler(e, 'Fehler beim Trennen');
    } finally { setGmailLaden(false); }
  }

  async function connectSmtpAccount() {
    if (!smtpEmail.trim() || !smtpPassword.trim()) { toast('E-Mail und Passwort eingeben', 'error'); return; }
    setSmtpLaden(true);
    try {
      const data = await apiPost<any>('/api/mail/accounts',
        { provider: smtpProvider, email: smtpEmail.trim(), password: smtpPassword });
      toast(`${smtpEmail} verbunden`, 'success');
      setSmtpKonten(prev => [...prev, data]);
      setSmtpEmail(''); setSmtpPassword(''); setSmtpProvider('');
    } catch (e) { zeigeApiFehler(e, 'Verbindung fehlgeschlagen'); } finally { setSmtpLaden(false); }
  }

  async function removeSmtpAccount(id: number) {
    try {
      await apiDelete(`/api/mail/accounts/${id}`);
      setSmtpKonten(prev => prev.filter(k => k.id !== id));
      toast('Konto entfernt', 'success');
    } catch (e) { zeigeApiFehler(e, 'Konto entfernen fehlgeschlagen'); }
  }

  async function pushAktivieren() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast('Push nicht unterstützt in diesem Browser', 'error'); return;
    }
    setPushLaden(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast('Berechtigung verweigert', 'error'); return; }
      const keyRes = await fetch(`${API_BASE}/api/push/vapid-public-key`);
      const { publicKey } = await keyRes.json();
      if (!publicKey) { toast('VAPID-Key fehlt', 'error'); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const subJson = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      await apiPost('/api/push/subscribe', subJson);
      setPushAktiv(true);
      toast('Push-Benachrichtigungen aktiviert', 'success');
    } catch (e: any) {
      toast(e.message ?? 'Push konnte nicht aktiviert werden', 'error');
    } finally { setPushLaden(false); }
  }

  async function pushDeaktivieren() {
    setPushLaden(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await apiFetch('/api/push/unsubscribe', { method: 'DELETE', body: JSON.stringify({ endpoint: sub.endpoint }) });
        await sub.unsubscribe();
      }
      setPushAktiv(false);
      toast('Push-Benachrichtigungen deaktiviert', 'success');
    } catch (e) { zeigeApiFehler(e, 'Fehler beim Deaktivieren'); } finally { setPushLaden(false); }
  }

  async function speicherePushEinst(neu: typeof pushEinst) {
    setPushEinst(neu);
    await apiPut('/api/push/einstellungen', neu)
      .catch(e => zeigeApiFehler(e, 'Push-Einstellungen speichern fehlgeschlagen'));
  }

  async function sendEinladung() {
    if (!einladeModal || !einladeEmail.trim() || !einladeFach.trim()) return;
    setEinladeLaden(true); setEinladeFehler(''); setEinladeErfolg('');
    try {
      await apiPost('/api/klasse/einladen',
        { klasse_id: einladeModal.klasse_id, email: einladeEmail.trim(), fach: einladeFach.trim(), rolle: einladeRolle });
      setEinladeErfolg(`Einladung an ${einladeEmail.trim()} gesendet.`);
      setEinladeEmail(''); setEinladeFach('');
    } catch (e: any) {
      setEinladeFehler(e.message ?? 'Einladung fehlgeschlagen.');
    } finally {
      setEinladeLaden(false);
    }
  }

  async function akzeptiereEinladung(id: number) {
    try {
      await apiPost(`/api/einladung/${id}/akzeptieren`);
      setEinladungen(prev => prev.filter(e => e.id !== id));
      // Klassen neu laden
      fetch(`${API_BASE}/api/meine-klassen`, { headers: apiHeaders() })
        .then(leseFallback<KlasseFachEintrag[]>([], '/api/meine-klassen')).then(setMeineKlassen).catch(() => {});
    } catch (e) { zeigeApiFehler(e, 'Einladung annehmen fehlgeschlagen'); }
  }

  async function lehneEinladungAb(id: number) {
    try {
      await apiPost(`/api/einladung/${id}/ablehnen`);
      setEinladungen(prev => prev.filter(e => e.id !== id));
    } catch (e) { zeigeApiFehler(e, 'Einladung ablehnen fehlgeschlagen'); }
  }

  function fachZurNeuListe() {
    const fach = neuAktuellesFach.trim();
    if (!fach) return;
    if (neuFaecher.includes(fach)) { toast('Fach bereits in der Liste', 'info'); return; }
    setNeuFaecher(prev => [...prev, fach]);
    setNeuAktuellesFach('');
  }

  async function handleKlasseMitFaechernAnlegen(): Promise<boolean> {
    if (!neuKlasse.trim() || neuFaecher.length === 0) return false;
    setKlassenLaden(true);
    const neuEintraege: KlasseFachEintrag[] = [];
    try {
      for (const fach of neuFaecher) {
        try {
          neuEintraege.push(await apiPost<KlasseFachEintrag>('/api/klasse-fach', {
            klasse_name: neuKlasse.trim(),
            schuljahr:   neuSchuljahr.trim() || undefined,
            fach,
            rolle:       neuRolle,
          }));
        } catch (e) { zeigeApiFehler(e, `Fach "${fach}" anlegen fehlgeschlagen`); }
      }
      if (neuEintraege.length > 0) {
        setMeineKlassen(prev => [...prev, ...neuEintraege]);
        setNeuKlasse('');
        setNeuFaecher([]);
        setNeuAktuellesFach('');
        setNeuSchuljahr('');
        setNeuRolle('fachlehrer');
        onKlassenReload?.();
      }
      return neuEintraege.length > 0;
    } finally {
      setKlassenLaden(false);
    }
  }

  // Welle K5: Fach direkt in der Klasse eintippen (statt Browser-Rückfrage)
  async function fachHinzufuegenZurKarte(klasseId: number, _klasseName: string, fach: string): Promise<boolean> {
    if (!fach || !fach.trim()) return false;
    const klasse = klassenGruppiert.find(k => k.klasse_id === klasseId);
    if (!klasse) return false;
    try {
      const neu = await apiPost<KlasseFachEintrag>('/api/klasse-fach',
        { klasse_name: klasse.klasse_name, schuljahr: klasse.schuljahr, fach: fach.trim(), rolle: klasse.rolle });
      setMeineKlassen(prev => [...prev, neu]);
      onKlassenReload?.();
      toast(`Fach "${fach.trim()}" hinzugefügt`, 'success');
      return true;
    } catch (e) { zeigeApiFehler(e, 'Fehler beim Speichern'); return false; }
  }

  async function fachEntfernenVonKarte(eintragId: number, fach: string, klasseName: string) {
    if (!(await confirm(`"${fach}" aus ${klasseName} entfernen?`, { danger: true, confirmLabel: 'Entfernen' }))) return;
    try {
      await apiDelete(`/api/klasse-fach/${eintragId}`);
      setMeineKlassen(prev => prev.filter(k => k.id !== eintragId));
      onKlassenReload?.();
      toast(`Fach "${fach}" entfernt`, 'success');
    } catch (e) { zeigeApiFehler(e, 'Fehler beim Löschen'); }
  }

  async function klasseLoeschen(klasseId: number, klasseName: string) {
    const klasse = klassenGruppiert.find(k => k.klasse_id === klasseId);
    if (!klasse) return;
    if (!(await confirm(`Klasse "${klasseName}" mit allen ${klasse.faecher.length} Fächern entfernen?\nSchüler-Daten bleiben in der Klasse erhalten.`, { danger: true, confirmLabel: 'Entfernen' }))) return;
    try {
      for (const f of klasse.faecher) {
        await apiDelete(`/api/klasse-fach/${f.id}`);
      }
      setMeineKlassen(prev => prev.filter(k => k.klasse_id !== klasseId));
      onKlassenReload?.();
      toast(`Klasse "${klasseName}" entfernt`, 'success');
    } catch (e) { zeigeApiFehler(e, 'Klasse entfernen fehlgeschlagen'); }
  }

  useEffect(() => {
    if (editKlasse) {
      setEditName(editKlasse.klasse_name);
      setEditRolle(editKlasse.rolle);
    }
  }, [editKlasse]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleEditSpeichern() {
    if (!editKlasse) return;
    setEditLaden(true);
    try {
      if (editName.trim() !== editKlasse.klasse_name) {
        try { await apiPut(`/api/klassen/${editKlasse.klasse_id}`, { name: editName.trim() }); }
        catch (e) { zeigeApiFehler(e, 'Fehler beim Speichern des Namens'); return; }
      }
      if (editRolle !== editKlasse.rolle) {
        try { await apiPut(`/api/klasse-fach/${editKlasse.id}`, { fach: editKlasse.fach, rolle: editRolle }); }
        catch (e) { zeigeApiFehler(e, 'Fehler beim Speichern der Rolle'); return; }
      }
      // Alle Einträge dieser Klasse in der lokalen Liste aktualisieren
      setMeineKlassen(prev => prev.map(k =>
        k.klasse_id === editKlasse.klasse_id
          ? { ...k, klasse_name: editName.trim(), rolle: editRolle }
          : k
      ));
      onKlassenReload?.();
      setEditKlasse(null);
    } finally {
      setEditLaden(false);
    }
  }

  function toggleDemo() {
    onKonfigUpdate({ ...konfig, demoModus: !konfig.demoModus });
  }

  // ── Welle K5: Übersicht mit Bereichen (Handy) bzw. zwei Spalten (PC) ──
  const isMobile = useIsMobile();
  const zeitraster = useZeitraster();
  const [bereichRoh, setBereichRoh] = useState<BereichKey | null>(() => {
    try { if (sessionStorage.getItem('ld-einst-ziel') === 'unterrichtszeiten') return 'zeiten'; } catch { /* egal */ }
    return null;
  });
  const bereich: BereichKey | null = bereichRoh ?? (isMobile ? null : 'konto');
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [schulfreiAnzahl, setSchulfreiAnzahl] = useState<number | null>(null);
  const [sprechzeit, setSprechzeit] = useState<Sprechzeit['wochentag_fenster'] | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [fachNeu, setFachNeu] = useState<{ klasseId: number; text: string } | null>(null);
  useEffect(() => {
    apiGet<{ id: number }[]>('/api/schulfreie-tage').then(t => setSchulfreiAnzahl(t.length)).catch(() => {});
    apiGet<any>('/api/lehrer/sprechzeit').then(d => setSprechzeit(d?.settings?.wochentag_fenster ?? {})).catch(() => {});
  }, [bereich]);

  // Handy: Bereich = eigener Verlaufseintrag (Zurück-Taste führt zur Übersicht)
  useEffect(() => {
    const zurueck = (e: PopStateEvent) => { if (e.state?.ldScreen === 'einstellungen') setBereichRoh((e.state?.ldBereich as BereichKey) ?? null); };
    window.addEventListener('popstate', zurueck);
    return () => window.removeEventListener('popstate', zurueck);
  }, []);
  function oeffneBereich(k: BereichKey) {
    if (k === 'datenschutz') { globalNav('datenschutz'); return; }
    if (k === 'archiv') { globalNav('archiv'); return; }
    if (k === 'admin') { globalNav('admin'); return; }
    if (k === 'abmelden') { onAbmelden(); return; }
    if (isMobile) window.history.pushState({ ldScreen: 'einstellungen', ldBereich: k }, '', window.location.pathname);
    setBereichRoh(k);
    window.scrollTo(0, 0);
  }
  function zurUebersicht() {
    if (window.history.state?.ldBereich) window.history.back(); else setBereichRoh(null);
  }

  const anredeText = anrede === 'frau' ? 'Frau' : anrede === 'herr' ? 'Herr' : '';
  const nachname = (nutzer?.nachname ?? accountName.split(' ').slice(-1)[0] ?? '').trim();
  const initialen = accountName.split(/\s+/).filter(Boolean).map(t => t[0]).slice(0, 2).join('').toUpperCase() || 'LD';
  const geteiltText = (() => {
    const geteilt = meineEigenenKlassen.map(k => ({ k, m: (mitgliederMap[k.klasse_id] ?? []).filter(m => m.id !== Number(nutzer?.id ?? 0)) })).filter(x => x.m.length);
    if (einladungen.length) return `${einladungen.length} ${einladungen.length === 1 ? 'Einladung' : 'Einladungen'} offen`;
    if (geteilt.length) return geteilt.map(x => `${x.k.klasse_name} mit ${x.m.map(m => `${m.vorname[0]}. ${m.nachname}`).join(', ')}`).join('; ');
    return 'keine geteilt';
  })();
  const WERT: Partial<Record<BereichKey, string>> = {
    klassen: klassenGruppiert.map(k => k.klasse_name).join(', ') || 'noch keine',
    zeiten: `${zeitraster.standard ? 'Standardzeiten' : 'Eigene Zeiten'}, ${zeitraster.raster.length} Stunden`,
    schulfrei: schulfreiAnzahl == null ? '' : schulfreiAnzahl === 0 ? 'noch keine eingetragen' : `${schulfreiAnzahl} ${schulfreiAnzahl === 1 ? 'Tag' : 'Tage'}`,
    grenzwerte: `Achtung ab ${fzGrenzen.achtung}, kritisch ab ${fzGrenzen.kritisch} Stunden`,
    mail: gmailStatus == null ? '' : gmailStatus.verbunden ? (gmailStatus.email ?? 'Gmail verbunden') : 'Gmail nicht verbunden',
    sprechzeit: sprechzeitKurz(sprechzeit),
    geteilt: geteiltText,
    startseite: 'Widgets auf Heute',
    push: pushAktiv ? 'an' : 'aus',
    ki: 'Verbrauch in diesem Monat',
    kiverarb: kiErlaubt == null ? '' : kiErlaubt ? 'erlaubt' : 'aus',
    datenschutz: 'Export, Aktivität, Konto löschen',
    archiv: 'Vorherige Schuljahre',
    demo: konfig.demoModus ? 'Demo an' : 'Demo aus',
  };

  const gruppen = BEREICH_GRUPPEN.map(g => ({ ...g, eintraege: g.eintraege.filter(e => e.key !== 'admin' || nutzer?.is_admin) }));
  const info = bereich ? BEREICH_INFO[bereich] : null;

  // ── Inhalte der Bereiche (Handler und Endpunkte wie bisher) ──
  const inhalt = (k: BereichKey): React.ReactNode => {
    switch (k) {
      case 'konto': return (
        <>
          <LdFeld label="Name" htmlFor="ld-es-name">
            <LdEingabe id="ld-es-name" value={accountName} onChange={e => setAccountName(e.target.value)} />
          </LdFeld>
          <LdFeld label="Anrede" hinweis={anrede ? `Andere sehen „${anredeText} ${nachname || 'Nachname'}“, auch als Gruß in Mails an Eltern.` : 'Ohne Anrede steht dein voller Name da.'}>
            <LdChips aria-label="Anrede" wert={anrede} onChange={setAnrede}
              optionen={[{ wert: 'frau' as const, label: 'Frau' }, { wert: 'herr' as const, label: 'Herr' }, { wert: '' as const, label: 'keine' }]} />
          </LdFeld>
          <LdFeld label="Schule">
            <LdEingabe value={konfig.lehrer.schule || '–'} readOnly disabled aria-label="Schule" />
          </LdFeld>
          <LdFeld label="Schulform">
            <LdAuswahl aria-label="Schulform" value={accountSchulform} onChange={e => setAccountSchulform(e.target.value as Schulform)}>
              {(['Gymnasium', 'IGS', 'Realschule', 'Grundschule'] as Schulform[]).map(sf => <option key={sf} value={sf}>{sf}</option>)}
            </LdAuswahl>
          </LdFeld>
          <EinstSpeichern onClick={async () => {
            onKonfigUpdate({ ...konfig, lehrer: { ...konfig.lehrer, name: accountName.trim(), schulform: accountSchulform } });
            try {
              await apiPut('/api/lehrer/einstellungen', { anrede: anrede || null });   // Welle Z2
              toast('Einstellungen gespeichert', 'success');
            } catch (e) { zeigeApiFehler(e, 'Anrede konnte nicht gespeichert werden'); }
          }} />
        </>
      );

      case 'klassen': return (
        <>
          {klassenGruppiert.length === 0 && <p className="ld-fe-text">Noch keine Klasse. Lege deine erste Klasse an.</p>}
          <div className="ld-es-klassen">
            {klassenGruppiert.map(k => (
              <div key={k.klasse_id} className="ld-es-klasse">
                <div className="ld-es-klasse__kopf">
                  <b>{k.klasse_name}</b>
                  <span className="ld-es-rolle">{k.rolle === 'klassenleiter' ? 'Klassenleitung' : 'Fachlehrer'}</span>
                  {Number(konfig.aktiveKlasseId) === Number(k.klasse_id) && <span className="ld-es-rolle ld-es-rolle--leise">aktiv</span>}
                  {k.konsistenzWarnung && <span className="ld-es-warn" title="Gemischte Rollen in dieser Klasse, bitte prüfen"><MIcon name="warning" size={16} /></span>}
                  <span className="ld-es-klasse__menue">
                    <KkMenu eintraege={[
                      { label: 'Umbenennen oder Rolle ändern', icon: 'edit', onClick: () => setEditKlasse(k.faecher[0] ? { ...k.faecher[0], klasse_id: k.klasse_id, klasse_name: k.klasse_name, rolle: k.rolle, schuljahr: k.schuljahr } as KlasseFachEintrag : null) },
                      { label: 'Klasse entfernen', icon: 'delete', onClick: () => klasseLoeschen(k.klasse_id, k.klasse_name) },
                    ]} />
                  </span>
                </div>
                <div className="ld-es-faecher">
                  {k.faecher.map(f => (
                    <span key={f.id} className="ld-es-fach">
                      {f.fach}
                      <button type="button" aria-label={`${f.fach} aus ${k.klasse_name} entfernen`} onClick={() => fachEntfernenVonKarte(f.id, f.fach, k.klasse_name)}><MIcon name="close" size={15} /></button>
                    </span>
                  ))}
                  {fachNeu?.klasseId === k.klasse_id ? (
                    <span className="ld-es-fach-neu">
                      <LdEingabe autoFocus aria-label={`Fach in ${k.klasse_name}`} placeholder="z. B. Deutsch" value={fachNeu.text}
                        onChange={e => setFachNeu({ klasseId: k.klasse_id, text: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fachHinzufuegenZurKarte(k.klasse_id, k.klasse_name, fachNeu.text).then(ok => ok && setFachNeu(null)); } if (e.key === 'Escape') setFachNeu(null); }} />
                      <button type="button" className="ld-kl-link" disabled={!fachNeu.text.trim()} onClick={() => fachHinzufuegenZurKarte(k.klasse_id, k.klasse_name, fachNeu.text).then(ok => ok && setFachNeu(null))}>Hinzufügen</button>
                    </span>
                  ) : (
                    <button type="button" className="ld-es-fach ld-es-fach--plus" onClick={() => setFachNeu({ klasseId: k.klasse_id, text: '' })}><MIcon name="add" size={16} />Fach</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit ld-es-anlegen" onClick={() => setNeuOffen(true)}><MIcon name="add" size={18} />Klasse anlegen</button>
        </>
      );

      case 'zeiten': return <Unterrichtszeiten />;
      case 'schulfrei': return <SchulfreieTage />;

      case 'grenzwerte': return (
        <>
          <div className="ld-es-liste">
            {([
              { key: 'achtung' as const, label: 'Achtung ab', ton: 'amber', sub: 'Darunter unbedenklich' },
              { key: 'kritisch' as const, label: 'Kritisch ab', ton: 'rot', sub: '§68 HSchG, Meldepflicht' },
            ]).map(({ key, label, ton, sub }) => (
              <EinstZeile key={key} titel={<><i className={`ld-es-punkt ld-es-punkt--${ton}`} aria-hidden="true" />{label}</>} text={sub}
                rechts={<LdEingabe type="number" min={1} max={200} aria-label={label} className="ld-es-zahl" value={fzGrenzen[key]}
                  rechts={<span className="ld-es-einheit">Std.</span>}
                  onChange={e => { const val = parseInt(e.target.value, 10); if (!isNaN(val)) { setFzGrenzen(prev => ({ ...prev, [key]: val })); setFzGespeichert(false); } }} />} />
            ))}
            {/* Welle U3c: Stunden pro ganzem Fehltag */}
            <EinstZeile titel="Ein ganzer Fehltag zählt als" text="Gilt für Einträge, die als ganzer Tag erfasst werden (Diktat, Mail, Ausflug). Schon gespeicherte Stunden ändern sich nicht."
              rechts={<LdEingabe id="ld-fz-stunden-pro-tag" type="number" min={1} max={10} aria-label="Ein ganzer Fehltag zählt als" className="ld-es-zahl" value={fzStundenProTag}
                rechts={<span className="ld-es-einheit">Std.</span>}
                onChange={e => { const val = parseInt(e.target.value, 10); if (!isNaN(val)) { setFzStundenProTag(val); setFzGespeichert(false); } }} />} />
          </div>
          <EinstSpeichern aus={fzGespeichert} hinweis="Basis: §68 HSchG Hessen, Werte in Unterrichtsstunden." onClick={async () => {
            if (fzGrenzen.achtung >= fzGrenzen.kritisch) { toast('Achtung-Wert muss kleiner als Kritisch-Wert sein.', 'error'); return; }
            if (!(fzStundenProTag >= 1 && fzStundenProTag <= 10)) { toast('Ein ganzer Fehltag muss als 1 bis 10 Stunden zählen.', 'error'); return; }
            try {
              await apiPut('/api/lehrer/einstellungen',
                { schwellwertGelb: fzGrenzen.achtung, schwellwertRot: fzGrenzen.kritisch, stundenProTag: fzStundenProTag });
              localStorage.setItem(FEHLZEITEN_GRENZEN_KEY, JSON.stringify(fzGrenzen));
              setFzGespeichert(true);
              toast('Grenzwerte gespeichert', 'success');
            } catch (e) { zeigeApiFehler(e, 'Speichern fehlgeschlagen'); }
          }} />
        </>
      );

      case 'mail': return (
        <>
          <EinstAbschnitt titel="Gmail">
            <div className="ld-es-liste">
              <EinstZeile titel={gmailStatus?.verbunden ? gmailStatus.email : 'Gmail'}
                text={gmailStatus === null ? 'Wird geprüft …' : gmailStatus.verbunden ? 'Verbunden' : gmailStatus.status === 'getrennt' ? <span className="ld-es-warntext">Verbindung getrennt, bitte neu verbinden</span> : 'Nicht verbunden'}
                rechts={gmailStatus?.verbunden
                  ? <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit ld-es-klein" onClick={gmailTrennen} disabled={gmailLaden}>{gmailLaden ? 'Bitte warten …' : 'Trennen'}</button>
                  : <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-es-klein" onClick={gmailVerbinden} disabled={gmailLaden}>{gmailLaden ? 'Bitte warten …' : 'Verbinden'}</button>} />
              {/* Promotions-Filter (speichert sofort) */}
              <EinstZeile titel="Werbemails abrufen" text="Gmail-Kategorie „Werbung“ beim Abrufen einschließen"
                rechts={<EinstSchalter label="Werbemails abrufen" an={syncPromotions} onChange={() => {
                  const neu = !syncPromotions;
                  setSyncPromotions(neu);
                  apiPut('/api/lehrer/mail-einstellungen', { sync_promotions: neu ? 1 : 0 })
                    .catch(e => { setSyncPromotions(!neu); zeigeApiFehler(e, 'Einstellung konnte nicht gespeichert werden'); });
                }} />} />
            </div>
            <p className="ld-fe-text">Zugangsdaten werden verschlüsselt gespeichert. Die App sendet nur Mails, die du freigibst. Nur eine Gmail-Verbindung gleichzeitig.</p>
          </EinstAbschnitt>

          <EinstAbschnitt titel="Weitere Mail-Konten">
            {smtpKonten.length > 0 && (
              <div className="ld-es-liste">
                {smtpKonten.map(k => (
                  <EinstZeile key={k.id} titel={k.display_name || k.email}
                    text={k.status === 'getrennt' ? <span className="ld-es-warntext">Verbindung getrennt, bitte neu verbinden</span> : k.provider}
                    rechts={<button type="button" className="ld-kl-link" style={{ color: 'var(--ld-danger-text)' }} onClick={() => removeSmtpAccount(k.id)}>Entfernen</button>} />
                ))}
              </div>
            )}
            <LdChips aria-label="Mail-Konto verbinden" wert={smtpProvider || null} onChange={key => {
              if (key === 'gmail') { gmailVerbinden(); return; }
              // Welle H3/U7: App-Passwort-Postfächer bewirken noch nichts (kein Abruf/Versand) → nicht anbieten
            }} optionen={[{ wert: 'gmail', label: 'Gmail' }]} />
            <p className="ld-fe-text">Web.de, GMX, T-Online, Outlook und andere kommen später.</p>
            {smtpProvider && smtpProvider !== 'gmail' && (
              <div className="ld-es-form">
                <LdEingabe placeholder="E-Mail-Adresse" aria-label="E-Mail-Adresse" value={smtpEmail} onChange={e => setSmtpEmail(e.target.value)} />
                <LdEingabe placeholder="App-Passwort" aria-label="App-Passwort" type="password" value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} />
                {smtpPresets[smtpProvider]?.note && <p className="ld-fe-text">{smtpPresets[smtpProvider].note}</p>}
                <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" onClick={connectSmtpAccount} disabled={smtpLaden || !smtpEmail.trim() || !smtpPassword.trim()}>
                  {smtpLaden ? 'Bitte warten …' : 'Verbinden'}
                </button>
              </div>
            )}
          </EinstAbschnitt>
        </>
      );

      case 'sprechzeit': return <ElternSprechzeitSettings onGeaendert={n => setSprechzeit(n.wochentag_fenster)} />;

      case 'geteilt': return (
        <>
          {meineEigenenKlassen.map(kl => {
            const mitglieder = (mitgliederMap[kl.klasse_id] ?? []).filter(m => m.id !== Number(nutzer?.id ?? 0));
            return (
              <EinstAbschnitt key={kl.klasse_id} titel={`Klasse ${kl.klasse_name}`}>
                {mitglieder.length === 0 ? <p className="ld-fe-text">Noch mit niemandem geteilt.</p> : (
                  <div className="ld-es-liste">
                    {mitglieder.map(m => <EinstZeile key={m.id} titel={`${m.vorname[0]}. ${m.nachname}`} text={m.fach} />)}
                  </div>
                )}
                <button type="button" className="ld-kl-link" style={{ alignSelf: 'flex-start' }} onClick={() => { setEinladeModal({ klasse_id: kl.klasse_id, klasse_name: kl.klasse_name }); setEinladeFehler(''); setEinladeErfolg(''); }}>
                  <MIcon name="person_add" size={17} />Lehrkraft einladen
                </button>
              </EinstAbschnitt>
            );
          })}
          {meineKlassen.filter(k => k.rolle === 'fachlehrer').length > 0 && (
            <EinstAbschnitt titel="Mit dir geteilt">
              <div className="ld-es-liste">
                {meineKlassen.filter(k => k.rolle === 'fachlehrer').map(k => <EinstZeile key={k.id} titel={k.klasse_name} text={`${k.fach}, Fachlehrer`} />)}
              </div>
            </EinstAbschnitt>
          )}
          {meineEigenenKlassen.length === 0 && meineKlassen.filter(k => k.rolle === 'fachlehrer').length === 0 && (
            <p className="ld-fe-text">Keine Klassen zum Teilen. Lege zuerst eine Klasse mit Rolle Klassenleitung an.</p>
          )}
          {einladungen.length > 0 && (
            <EinstAbschnitt titel={`Einladungen (${einladungen.length})`}>
              <div className="ld-es-liste">
                {einladungen.map(e => (
                  <EinstZeile key={e.id} titel={`${e.einladender_vorname[0]}. ${e.einladender_nachname} lädt dich ein`}
                    text={`Klasse ${e.klasse_name}, ${e.fach}, ${e.rolle === 'klassenleiter' ? 'Klassenleitung' : 'Fachlehrer'}`}
                    rechts={<span className="ld-es-paar">
                      <button type="button" className="ld-kl-link" style={{ color: 'var(--ld-text-secondary)' }} onClick={() => lehneEinladungAb(e.id)}>Ablehnen</button>
                      <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-es-klein" onClick={() => akzeptiereEinladung(e.id)}>Annehmen</button>
                    </span>} />
                ))}
              </div>
            </EinstAbschnitt>
          )}
        </>
      );

      case 'startseite': return (
        <>
          <p className="ld-fe-text">Widgets auf „Heute“ anordnen, vergrößern oder ausblenden. Die Auswahl machst du direkt auf der Startseite.</p>
          <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-es-anlegen" onClick={oeffneBoardAnpassen}><MIcon name="dashboard_customize" size={18} />Startseite anpassen</button>
        </>
      );

      case 'push': return (
        <>
          <div className="ld-es-liste">
            <EinstZeile titel="Push-Benachrichtigungen" text={pushAktiv ? 'Auf diesem Gerät aktiv' : 'Nicht aktiv'}
              rechts={<EinstSchalter label="Push-Benachrichtigungen" an={pushAktiv} aus={pushLaden} onChange={pushAktiv ? pushDeaktivieren : pushAktivieren} />} />
            {pushAktiv && ([
              { key: 'fehlzeiten'       as const, label: 'Fehlzeiten-Alarm',            beschr: 'Bei Überschreiten eines Grenzwerts' },
              { key: 'entschuldigungen' as const, label: 'Überfällige Entschuldigungen', beschr: 'Täglich 07:00 Uhr' },
              { key: 'aktionen'         as const, label: 'Fällige Aktionen',             beschr: 'Täglich 07:00 Uhr' },
              { key: 'nachschreiben'    as const, label: 'Nachschreib-Termine',          beschr: 'Täglich 07:00 Uhr' },
              { key: 'mdl_luecken'      as const, label: 'Mitarbeit-Lücken',             beschr: 'Montags 07:00 Uhr, Klassen ohne Einträge in der letzten Woche' },
              { key: 'mdl_vorschlaege'  as const, label: 'Offene Note-Vorschläge',       beschr: 'Montags 07:00 Uhr, Vorschläge nach unentschuldigtem Fehlen' },
            ]).map(({ key, label, beschr }) => (
              <EinstZeile key={key} titel={label} text={beschr}
                rechts={<EinstSchalter label={label} an={pushEinst[key]} onChange={() => speicherePushEinst({ ...pushEinst, [key]: !pushEinst[key] })} />} />
            ))}
          </div>
          <p className="ld-fe-text">Benachrichtigungen kommen auch, wenn die App geschlossen ist. Gilt nur für dieses Gerät.</p>
        </>
      );

      case 'ki': return <KiKontingentePanel />;

      case 'kiverarb': return (
        <>
          <div className="ld-es-liste">
            <EinstZeile titel="KI-Verarbeitung erlauben"
              text="Ohne sie funktionieren Erzählen, Mail-Antworten, Zeugnis-Entwurf, Stoffplan-Vorschläge und Klassenbuch-Autofill nicht. Deine Daten auf dem Server bleiben unverändert."
              rechts={<EinstSchalter label="KI-Verarbeitung erlauben" an={!!kiErlaubt} aus={kiToggleLaden} onChange={async () => {
                if (kiToggleLaden) return;
                setKiToggleLaden(true);
                const neu = !(kiErlaubt ?? false);
                try {
                  await apiPost('/api/einwilligung/aktualisieren', { ki_verarbeitung: neu });
                  setKiErlaubt(neu); toast(neu ? 'KI-Verarbeitung aktiviert' : 'KI-Verarbeitung deaktiviert');
                } catch (e) { zeigeApiFehler(e, 'Fehler beim Speichern'); } finally { setKiToggleLaden(false); }
              }} />} />
          </div>
          {kiErlaubt === false && <p className="ld-es-warntext"><MIcon name="info" size={16} />Die KI-Funktionen sind aus. Erzählen und andere KI-Funktionen stehen nicht zur Verfügung.</p>}
          <button type="button" className="ld-kl-link" style={{ alignSelf: 'flex-start', color: 'var(--ld-text-secondary)' }} onClick={() => {
            apiPost('/api/einwilligung/widerrufen')
              .then(() => { setKiErlaubt(false); toast('KI-Einwilligung widerrufen'); })
              .catch(e => zeigeApiFehler(e, 'Widerruf fehlgeschlagen'));
          }}>Einwilligung widerrufen</button>
        </>
      );

      case 'demo': return (
        <div className="ld-es-liste">
          <EinstZeile titel="Demo-Modus" text="Testdaten mit 25 Beispiel-Schüler·innen"
            rechts={<EinstSchalter label="Demo-Modus" an={!!konfig.demoModus} onChange={toggleDemo} />} />
          <EinstZeile gefahr titel="Neu starten" text="Einstellungen auf diesem Gerät zurücksetzen und das Onboarding erneut starten. Deine Daten auf dem Server bleiben erhalten."
            rechts={!bestaetigung
              ? <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ist-gefahr ld-es-klein" onClick={() => setBestaetigung(true)}>Neu starten</button>
              : <span className="ld-es-paar">
                  <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit ld-es-klein" onClick={() => setBestaetigung(false)}>Abbrechen</button>
                  <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ist-gefahr ld-es-klein" onClick={onNeuStart}>Ja, zurücksetzen</button>
                </span>} />
        </div>
      );
      default: return null;
    }
  };

  const zeileKnopf = (e: BereichEintrag, gewaehlt: boolean, kompakt: boolean) => (
    <button key={e.key} type="button" className={`ld-es-nav${gewaehlt ? ' ist-an' : ''}${e.key === 'abmelden' ? ' ld-es-nav--abmelden' : ''}`}
      aria-current={gewaehlt ? 'page' : undefined} onClick={() => oeffneBereich(e.key)}>
      <span className="ld-es-nav__icon" aria-hidden="true"><MIcon name={e.icon} size={20} /></span>
      <span className="ld-es-nav__text">
        <b>{e.titel}</b>
        {!kompakt && WERT[e.key] && <small>{WERT[e.key]}</small>}
      </span>
      {kompakt && WERT[e.key] && ['klassen', 'mail', 'push'].includes(e.key) && <small className="ld-es-nav__wert">{e.key === 'klassen' ? klassenGruppiert.length : WERT[e.key]}</small>}
      {!kompakt && e.key !== 'abmelden' && <MIcon name="chevron_right" size={20} className="ld-es-nav__pfeil" />}
    </button>
  );
  const kontoKarte = (kompakt: boolean) => (
    <button type="button" className={`ld-es-konto${!kompakt ? ' ld-kl-karte' : ''}${kompakt && bereich === 'konto' ? ' ist-an' : ''}`} onClick={() => oeffneBereich('konto')} aria-current={bereich === 'konto' ? 'page' : undefined}>
      <span className="ld-es-konto__avatar" aria-hidden="true">{initialen}</span>
      <span className="ld-es-nav__text">
        <b>{kompakt ? 'Konto und Schule' : accountName || 'Konto'}</b>
        <small>{kompakt ? 'Name, Anrede, Schulform' : [anredeText ? `${anredeText} ${nachname}` : null, konfig.lehrer.schule].filter(Boolean).join(', ')}</small>
      </span>
      {!kompakt && <MIcon name="chevron_right" size={20} className="ld-es-nav__pfeil" />}
    </button>
  );
  const bereichInhalt = bereich && info && (
    <div className="ld-es-bereich__inhalt">{inhalt(bereich)}</div>
  );

  return (
    <EinstSpeichernZiel.Provider value={isMobile && bereich ? slot : null}>
    <div className="ld-page-enter ld-est">
      {isMobile && !bereich && (
        <>
          <h1 className="ld-es__titel">Einstellungen</h1>
          {kontoKarte(false)}
          {gruppen.map(g => (
            <section key={g.titel} className="ld-es-gruppe" aria-label={g.titel}>
              <h2>{g.titel}</h2>
              <div className="ld-kl-karte ld-es-gruppe__karte">{g.eintraege.map(e => zeileKnopf(e, false, false))}</div>
            </section>
          ))}
          <div className="ld-kl-karte ld-es-gruppe__karte" style={{ marginTop: 16 }}>{zeileKnopf(ABMELDEN, false, false)}</div>
          <RechtsLinks style={{ margin: '16px 0 0' }} />
        </>
      )}

      {isMobile && bereich && info && (
        <div className="ld-es-seite">
          <div className="ld-es-seite__leiste">
            <button type="button" className="ld-kl-link ld-es-zurueck" onClick={zurUebersicht}><MIcon name="chevron_left" size={20} />Einstellungen</button>
            <span ref={setSlot} className="ld-es-seite__slot" />
          </div>
          <h1 className="ld-es__titel">{info.titel}</h1>
          {info.text && <p className="ld-es__kontext">{info.text}</p>}
          {bereichInhalt}
        </div>
      )}

      {!isMobile && (
        <>
          <div className="ld-es-pc__kopf">
            <h1 className="ld-es__titel">Einstellungen</h1>
            <span className="ld-es__kontext">{[accountName, konfig.lehrer.schule].filter(Boolean).join(', ')}</span>
          </div>
          <div className="ld-es-pc">
            <nav className="ld-kl-karte ld-es-pc__nav" aria-label="Bereiche der Einstellungen">
              {kontoKarte(true)}
              {gruppen.map(g => (
                <div key={g.titel} className="ld-es-pc__gruppe">
                  <h2>{g.titel}</h2>
                  {g.eintraege.map(e => zeileKnopf(e, bereich === e.key, true))}
                </div>
              ))}
              <div className="ld-es-pc__gruppe">{zeileKnopf(ABMELDEN, false, true)}</div>
            </nav>
            <section className="ld-kl-karte ld-es-pc__bereich" aria-label={info?.titel}>
              {info && <h2 className="ld-es-pc__titel">{info.titel}</h2>}
              {info?.text && <p className="ld-es__kontext">{info.text}</p>}
              {bereichInhalt}
            </section>
          </div>
          <RechtsLinks ausrichtung="flex-start" style={{ margin: '16px 0 0 4px' }} />
        </>
      )}

      {/* Einlade-Fenster */}
      <LdSheet open={!!einladeModal} onClose={() => setEinladeModal(null)} maxWidth={480}
        kopf={{ titel: 'Lehrkraft einladen', kontext: einladeModal ? `Klasse ${einladeModal.klasse_name}` : null }}
        fuss={<LdFensterFuss onAbbrechen={() => setEinladeModal(null)} haupt="Einladen" onHaupt={sendEinladung} laden={einladeLaden} aus={!einladeEmail.trim() || !einladeFach.trim()}
          links={<button type="button" className="ld-kl-link" title="Einladungslink in die Zwischenablage kopieren" onClick={() => {
            // Fallback: Einladungslink in Zwischenablage kopieren (z.B. wenn Gmail nicht konfiguriert)
            const base = window.location.origin;
            const link = `${base}/einladung?klasse=${einladeModal?.klasse_id ?? ''}&email=${encodeURIComponent(einladeEmail.trim())}&fach=${encodeURIComponent(einladeFach.trim())}&rolle=${einladeRolle}`;
            navigator.clipboard.writeText(link).then(() => toast('Link kopiert', 'success')).catch(() => toast('Kopieren fehlgeschlagen', 'error'));
          }}><MIcon name="content_copy" size={17} />Link kopieren</button>} />}>
        <LdFeld label="E-Mail der Lehrkraft" htmlFor="ld-einlade-mail">
          <LdEingabe id="ld-einlade-mail" value={einladeEmail} onChange={e => setEinladeEmail(e.target.value)} type="email" placeholder="kollege@schule.de" />
        </LdFeld>
        <LdFeld label="Fach" htmlFor="ld-einlade-fach">
          <LdEingabe id="ld-einlade-fach" value={einladeFach} onChange={e => setEinladeFach(e.target.value)} placeholder="z. B. Mathematik" />
        </LdFeld>
        <LdFeld label="Rolle">
          <LdChips aria-label="Rolle" wert={einladeRolle} onChange={setEinladeRolle}
            optionen={[{ wert: 'fachlehrer' as const, label: 'Fachlehrer/in' }, { wert: 'klassenleiter' as const, label: 'Mitklassenleitung' }]} />
        </LdFeld>
        {einladeFehler && <p className="ld-fe-fehler" role="alert">{einladeFehler}</p>}
        {einladeErfolg && <p className="ld-es-ok" role="status"><MIcon name="check" size={17} />{einladeErfolg}</p>}
      </LdSheet>

      {/* Klasse anlegen (K4-Fenster) */}
      <LdSheet open={neuOffen} onClose={() => setNeuOffen(false)} maxWidth={480} kopf={{ titel: 'Klasse anlegen' }}
        fuss={<LdFensterFuss onAbbrechen={() => setNeuOffen(false)} haupt={`Klasse anlegen${neuFaecher.length > 0 ? ` (${neuFaecher.length} ${neuFaecher.length === 1 ? 'Fach' : 'Fächer'})` : ''}`}
          laden={klassenLaden} aus={!neuKlasse.trim() || neuFaecher.length === 0}
          onHaupt={async () => { if (await handleKlasseMitFaechernAnlegen()) setNeuOffen(false); }} />}>
        <LdFeld label="Klasse" htmlFor="ld-neu-klasse">
          <LdEingabe id="ld-neu-klasse" value={neuKlasse} onChange={e => setNeuKlasse(e.target.value)} placeholder="z. B. 8a" />
        </LdFeld>
        <LdFeld label="Rolle">
          <LdChips aria-label="Rolle" wert={neuRolle} onChange={setNeuRolle}
            optionen={[{ wert: 'fachlehrer' as const, label: 'Fachlehrer' }, { wert: 'klassenleiter' as const, label: 'Klassenleitung' }]} />
        </LdFeld>
        <LdFeld label="Fächer" hinweis={neuFaecher.length === 0 ? 'Mindestens ein Fach eintragen.' : undefined}>
          {neuFaecher.length > 0 && (
            <div className="ld-es-faecher">
              {neuFaecher.map(f => (
                <span key={f} className="ld-es-fach">{f}
                  <button type="button" aria-label={`${f} entfernen`} onClick={() => setNeuFaecher(prev => prev.filter(x => x !== f))}><MIcon name="close" size={15} /></button>
                </span>
              ))}
            </div>
          )}
          <LdEingabe aria-label="Fach hinzufügen" value={neuAktuellesFach} onChange={e => setNeuAktuellesFach(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fachZurNeuListe(); } }} placeholder="Fach, z. B. Deutsch"
            rechts={<button type="button" className="ld-fe-icon" aria-label="Fach hinzufügen" disabled={!neuAktuellesFach.trim()} onClick={fachZurNeuListe}><MIcon name="add" size={20} /></button>} />
        </LdFeld>
      </LdSheet>

      {/* Klasse bearbeiten */}
      <LdSheet open={!!editKlasse} onClose={() => !editLaden && setEditKlasse(null)} maxWidth={480}
        kopf={{ titel: 'Klasse bearbeiten', kontext: editKlasse ? `Klasse ${editKlasse.klasse_name}` : null }}
        fuss={<LdFensterFuss onAbbrechen={() => setEditKlasse(null)} haupt="Speichern" onHaupt={handleEditSpeichern} laden={editLaden} aus={!editName.trim()} />}>
        <LdFeld label="Klassenname" htmlFor="ld-edit-klasse">
          <LdEingabe id="ld-edit-klasse" value={editName} onChange={e => setEditName(e.target.value)} placeholder="z. B. 10a" />
        </LdFeld>
        <LdFeld label="Rolle in dieser Klasse">
          <LdChips aria-label="Rolle in dieser Klasse" wert={editRolle} onChange={setEditRolle}
            optionen={[{ wert: 'fachlehrer' as const, label: 'Fachlehrer/in' }, { wert: 'klassenleiter' as const, label: 'Klassenleitung' }]} />
        </LdFeld>
      </LdSheet>
    </div>
    </EinstSpeichernZiel.Provider>
  );
}

// ── Bereiche der Einstellungen ──
type BereichKey = 'konto' | 'klassen' | 'zeiten' | 'schulfrei' | 'grenzwerte' | 'mail' | 'sprechzeit' | 'geteilt'
  | 'startseite' | 'push' | 'ki' | 'kiverarb' | 'datenschutz' | 'archiv' | 'demo' | 'admin' | 'abmelden';
type BereichEintrag = { key: BereichKey; titel: string; icon: string };
const BEREICH_GRUPPEN: { titel: string; eintraege: BereichEintrag[] }[] = [
  { titel: 'Unterricht', eintraege: [
    { key: 'klassen', titel: 'Meine Klassen und Fächer', icon: 'group' },
    { key: 'zeiten', titel: 'Unterrichtszeiten', icon: 'schedule' },
    { key: 'schulfrei', titel: 'Schulfreie Tage', icon: 'event_busy' },
    { key: 'grenzwerte', titel: 'Fehlzeiten-Grenzwerte', icon: 'warning' },
  ] },
  { titel: 'Eltern und Mails', eintraege: [
    { key: 'mail', titel: 'Mail-Konto', icon: 'mail' },
    { key: 'sprechzeit', titel: 'Eltern-Sprechzeit', icon: 'event_available' },
    { key: 'geteilt', titel: 'Geteilte Klassen', icon: 'diversity_3' },
  ] },
  { titel: 'App', eintraege: [
    { key: 'startseite', titel: 'Startseite anpassen', icon: 'dashboard_customize' },
    { key: 'push', titel: 'Benachrichtigungen', icon: 'notifications' },
    { key: 'ki', titel: 'KI und Kontingente', icon: 'auto_awesome' },
    { key: 'admin', titel: 'Admin-Konsole', icon: 'shield' },
  ] },
  { titel: 'Daten und Datenschutz', eintraege: [
    { key: 'kiverarb', titel: 'KI-Verarbeitung', icon: 'psychology' },
    { key: 'datenschutz', titel: 'Datenschutz und Konto', icon: 'lock' },
    { key: 'archiv', titel: 'Schuljahres-Archiv', icon: 'inventory_2' },
    { key: 'demo', titel: 'Demo-Modus und Neu starten', icon: 'restart_alt' },
  ] },
];
const ABMELDEN: BereichEintrag = { key: 'abmelden', titel: 'Abmelden', icon: 'logout' };
const BEREICH_INFO: Partial<Record<BereichKey, { titel: string; text?: string }>> = {
  konto: { titel: 'Konto und Schule', text: 'So erscheinst du bei Kolleginnen und Kollegen und in Mails an Eltern.' },
  klassen: { titel: 'Meine Klassen und Fächer', text: 'Deine Klassen mit deiner Rolle und deinen Fächern.' },
  zeiten: { titel: 'Unterrichtszeiten', text: 'Danach richten sich Heute, Stundenplan, Klassenbuch und Terminvorschläge.' },
  schulfrei: { titel: 'Schulfreie Tage', text: 'Diese Tage legt das Schulamt für jede Schule fest. LehrerDesk zählt sie dann nicht als Schultag.' },
  grenzwerte: { titel: 'Fehlzeiten-Grenzwerte', text: 'Ab wann eine Fehlzeit Aufmerksamkeit braucht, in Unterrichtsstunden.' },
  mail: { titel: 'Mail-Konto', text: 'Mails von Eltern direkt in LehrerDesk lesen und beantworten.' },
  sprechzeit: { titel: 'Eltern-Sprechzeit', text: 'Wann bist du für Elterngespräche erreichbar? Daraus und aus deinem Stundenplan schlägt LehrerDesk freie Termine vor.' },
  geteilt: { titel: 'Geteilte Klassen', text: 'Lehrkräfte einladen und Klassen gemeinsam nutzen.' },
  startseite: { titel: 'Startseite anpassen' },
  push: { titel: 'Benachrichtigungen' },
  ki: { titel: 'KI und Kontingente', text: 'Dein Verbrauch in diesem Monat.' },
  kiverarb: { titel: 'KI-Verarbeitung' },
  demo: { titel: 'Demo-Modus und Neu starten' },
};
